import { ButtonHTMLAttributes, HTMLAttributes, useCallback, useEffect, useState } from 'react'
import exportFromJSON from 'export-from-json'
import {
  ArrowDownUpIcon,
  DownloadIcon,
  FileDownIcon,
  TrashIcon,
  UploadIcon,
  XIcon
} from 'lucide-react'
import { useShallow } from 'zustand/shallow'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/base/tooltip'
import Typography from '@/components/base/typography'
import { useAppStore } from '@/contexts/store'
import { useToast } from '@/contexts/toast'
import { useSSRWishlist } from '@/hooks'
import { selectEnabledWishlist, useWishlistStore } from '@/store/wishlist'

import Button from './button'
import WishlistDialog from './wishlist-dialog'
import { createWishlistPdf, WishlistPdfRenderer } from './wishlist-pdf'
import { cn } from '@/lib/utils'

export type WishlistToolButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  tooltip?: string
}

const WishlistToolButton = ({ tooltip, className, ...props }: WishlistToolButtonProps) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className={cn(
            'relative flex size-[4.4rem] cursor-pointer items-center justify-center overflow-hidden rounded-[0.5rem]',
            'before:absolute before:left-0 before:top-0 before:size-full before:cursor-pointer before:rounded-[0.5rem] before:border before:border-dark/25',
            'transition-colors duration-300 ease-out-cubic hover:bg-light/70',
            'disabled:pointer-events-none disabled:opacity-40',
            'focus-ring-dark',
            className
          )}
          type="button"
          {...props}
        />
      </TooltipTrigger>
      {tooltip ? (
        <TooltipContent>
          <Typography variant="breadcrumbs" paragraph>
            {tooltip}
          </Typography>
        </TooltipContent>
      ) : null}
    </Tooltip>
  )
}

export type WishlistToolsProps = HTMLAttributes<HTMLDivElement>

const WishlistTools = ({ className, ...props }: WishlistToolsProps) => {
  const ssrWishlist = useSSRWishlist()
  const wishlist = selectEnabledWishlist(ssrWishlist)
  const { strings } = useAppStore()
  const { showToast } = useToast()
  const [loadWishlistFromFile, clearWishlist] = useWishlistStore(
    useShallow((state) => [state.loadWishlistFromFile, state.clearWishlist])
  )
  const [activeDialog, setActiveDialog] = useState<'transfer' | 'alert' | null>(null)
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const debug = false
  /* const debug =
    process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_WISHLIST_PDF_DEBUG === 'true' */

  const handleImportWishlist = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.currentTarget
      const file = input.files?.[0]
      if (file) {
        try {
          await loadWishlistFromFile(file)
          setActiveDialog(null)
          showToast({
            variant: 'success',
            message: strings?.wishlist?.import_export_import_success_message
          })
        } catch {
          showToast({
            variant: 'error',
            message: strings?.wishlist?.import_export_import_error_message
          })
        }
      }
      input.value = ''
    },
    [loadWishlistFromFile, showToast, strings?.wishlist]
  )

  const handleExportWishlist = useCallback(() => {
    try {
      exportFromJSON({
        data: ssrWishlist,
        fileName: 'coloridecora-wishlist',
        exportType: exportFromJSON.types.json
      })
      setActiveDialog(null)
      showToast({
        variant: 'success',
        message: strings?.wishlist?.import_export_export_success_message
      })
    } catch {
      showToast({ variant: 'error', message: strings?.wishlist?.import_export_export_error_message })
    }
  }, [showToast, ssrWishlist, strings?.wishlist])

  const handleEmptyWishlist = useCallback(() => {
    try {
      clearWishlist()
      setActiveDialog(null)
      showToast({ variant: 'success', message: strings?.wishlist?.empty_success_message })
    } catch {
      showToast({ variant: 'error', message: strings?.wishlist?.empty_error_message })
    }
  }, [clearWishlist, showToast, strings?.wishlist])

  const handleDownloadPdf = useCallback(async () => {
    if (isDownloading || !wishlist.length) return

    setIsDownloading(true)

    try {
      if (debug) {
        setIsPdfPreviewOpen(true)
        showToast({ variant: 'success', message: 'Anteprima PDF aperta' })
        return
      }

      const blob = await createWishlistPdf(wishlist)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `coloridecora-wishlist-${Date.now()}.pdf`
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      showToast({ variant: 'success', message: strings?.wishlist?.pdf_download_success_message })
    } catch {
      showToast({ variant: 'error', message: strings?.wishlist?.pdf_download_error_message })
    } finally {
      setIsDownloading(false)
    }
  }, [debug, isDownloading, showToast, wishlist, strings?.wishlist])

  useEffect(() => {
    if (!activeDialog && !isPdfPreviewOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveDialog(null)
        setIsPdfPreviewOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeDialog, isPdfPreviewOpen])

  return (
    <div className={cn('flex items-center space-x-[1rem]', className)} {...props}>
      <WishlistToolButton
        tooltip={strings?.wishlist?.import_export_tooltip}
        aria-label={strings?.wishlist?.import_export_tooltip}
        onClick={() => setActiveDialog('transfer')}
      >
        <ArrowDownUpIcon size={16} strokeWidth={1} />
      </WishlistToolButton>

      <WishlistToolButton
        tooltip={strings?.wishlist?.pdf_download_tooltip}
        aria-label={strings?.wishlist?.pdf_download_tooltip}
        onClick={handleDownloadPdf}
        disabled={isDownloading || !wishlist.length}
      >
        <FileDownIcon size={16} strokeWidth={1} />
      </WishlistToolButton>

      <WishlistToolButton
        tooltip={strings?.wishlist?.empty_tooltip}
        aria-label={strings?.wishlist?.empty_tooltip}
        onClick={() => setActiveDialog('alert')}
        disabled={!wishlist.length}
      >
        <TrashIcon size={16} strokeWidth={1} />
      </WishlistToolButton>

      <WishlistDialog
        open={activeDialog === 'transfer'}
        title={strings?.wishlist?.import_export_dialog_title}
        onClose={() => setActiveDialog(null)}
        footer={
          <div className="flex space-x-[1rem]">
            <Button
              asChild
              variant="secondary"
              size="small"
              endIcon={<UploadIcon size={16} strokeWidth={1} />}
              className={cn(
                'relative cursor-pointer overflow-hidden',
                'min-w-auto w-auto before:bg-white',
                'focus-within-ring-dark'
              )}
            >
              <label>
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleImportWishlist}
                  className="sr-only focus:outline-none"
                />
                <Typography variant="button" className="!m-0">
                  {strings?.wishlist?.import_export_dialog_import}
                </Typography>
              </label>
            </Button>

            {wishlist.length > 0 ? (
              <Button
                type="button"
                variant="primary"
                size="small"
                className={cn('min-w-auto w-auto')}
                endIcon={<DownloadIcon size={16} strokeWidth={1} />}
                onClick={handleExportWishlist}
              >
                <Typography variant="button">
                  {strings?.wishlist?.import_export_dialog_export}
                </Typography>
              </Button>
            ) : null}
          </div>
        }
      >
        <Typography variant="small">{strings?.wishlist?.import_export_dialog_message}</Typography>
      </WishlistDialog>

      <WishlistDialog
        open={activeDialog === 'alert'}
        title={strings?.wishlist?.empty_dialog_title}
        onClose={() => setActiveDialog(null)}
        footer={
          <div className="flex space-x-[1rem]">
            <Button
              type="button"
              variant="secondary"
              size="small"
              className={cn('min-w-auto w-auto bg-white')}
              onClick={() => setActiveDialog(null)}
            >
              <Typography variant="button">{strings?.wishlist?.empty_dialog_cancel}</Typography>
            </Button>

            <Button
              type="button"
              variant="alert"
              size="small"
              className={cn('min-w-auto w-auto')}
              endIcon={<TrashIcon size={16} strokeWidth={1} />}
              onClick={handleEmptyWishlist}
            >
              <Typography variant="button">{strings?.wishlist?.empty_dialog_confirm}</Typography>
            </Button>
          </div>
        }
      >
        <Typography variant="small">{strings?.wishlist?.empty_dialog_message}</Typography>
      </WishlistDialog>

      {isPdfPreviewOpen ? (
        <div className="fixed inset-0 z-dialog !m-0 bg-white">
          <WishlistPdfRenderer />
          <button
            type="button"
            onClick={() => setIsPdfPreviewOpen(false)}
            className="absolute right-[1rem] top-[1rem] flex size-[4.4rem] items-center justify-center rounded-[0.5rem] border border-white/50 bg-dark/80 text-white"
            aria-label={strings?.labels?.close}
          >
            <XIcon size={18} />
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default WishlistTools
